import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from "react";

const STORAGE_KEY = "kontractai_client_scope";
const ALL_SENTINEL = "all" as const;

export type ClientChoice = number | typeof ALL_SENTINEL;

export interface ClientScopeOrg {
  id: string;
  name: string;
  org_type: "firm" | "client" | "internal";
  client_id: number | null;
}

export interface ClientScopeAvailable {
  id: number;
  name: string;
}

export type Edition = "firm" | "in_house";

export interface ClientScopeValue {
  loading: boolean;
  organization: ClientScopeOrg | null;
  availableClients: ClientScopeAvailable[];
  canPickAll: boolean;
  selected: ClientChoice;
  setSelected: (next: ClientChoice) => void;
  refresh: () => Promise<void>;
  edition: Edition;
  // Whether the AI usage and costs screen exists on this deployment. Decided
  // by the server, never inferred from `edition` — see aiSpendAvailability.js.
  aiSpendAvailable: boolean;
  tenantPrefix: string;
}

const noopValue: ClientScopeValue = {
  loading: true,
  organization: null,
  availableClients: [],
  canPickAll: false,
  selected: ALL_SENTINEL,
  setSelected: () => {},
  refresh: async () => {},
  edition: "firm",
  aiSpendAvailable: false,
  tenantPrefix: "",
};

const ClientScopeCtx = createContext<ClientScopeValue>(noopValue);

export function readSelectedFromStorage(): ClientChoice {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw || raw === ALL_SENTINEL) return ALL_SENTINEL;
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : ALL_SENTINEL;
  } catch {
    return ALL_SENTINEL;
  }
}

function writeSelectedToStorage(choice: ClientChoice) {
  try {
    if (choice === ALL_SENTINEL) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, String(choice));
  } catch {
    /* sessionStorage unavailable — safe to ignore; header just won't be sent */
  }
}

export function ClientScopeProvider({ token, children }: { token: string | null; children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState<ClientScopeOrg | null>(null);
  const [availableClients, setAvailableClients] = useState<ClientScopeAvailable[]>([]);
  const [canPickAll, setCanPickAll] = useState(false);
  const [selected, setSelectedState] = useState<ClientChoice>(readSelectedFromStorage());
  const [edition, setEdition] = useState<Edition>("firm");
  // Defaults to FALSE. Unknown must read as "not available": the direction
  // that hides a screen is safe, the direction that shows it is not.
  const [aiSpendAvailable, setAiSpendAvailable] = useState(false);
  const [tenantPrefix, setTenantPrefix] = useState<string>("");

  const refresh = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("/api/users/me/client-scope", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setLoading(false);
        return;
      }
      const data = await res.json();
      setOrganization(data.organization || null);
      setAvailableClients(data.available_clients || []);
      setCanPickAll(!!data.can_pick_all);
      setEdition(data.edition === "in_house" ? "in_house" : "firm");
      setAiSpendAvailable(!!data.ai_spend_available);
      setTenantPrefix((data.tenant_prefix || "").toUpperCase());

      // Reconcile persisted selection against the freshly loaded scope. If the
      // stored client is no longer in the user's scope (revoked, etc.), fall
      // back to "all" rather than sending a header that the backend will 403.
      const stored = readSelectedFromStorage();
      const ids = new Set<number>((data.available_clients || []).map((c: ClientScopeAvailable) => c.id));
      let next: ClientChoice = ALL_SENTINEL;
      if (stored !== ALL_SENTINEL && ids.has(stored)) next = stored;
      else if (!data.can_pick_all && (data.available_clients || []).length === 1) next = data.available_clients[0].id;
      setSelectedState(next);
      writeSelectedToStorage(next);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setSelected = useCallback((next: ClientChoice) => {
    setSelectedState(next);
    writeSelectedToStorage(next);
  }, []);

  const value = useMemo<ClientScopeValue>(
    () => ({ loading, organization, availableClients, canPickAll, selected, setSelected, refresh, edition, tenantPrefix, aiSpendAvailable }),
    [loading, organization, availableClients, canPickAll, selected, setSelected, refresh, edition, tenantPrefix, aiSpendAvailable]
  );

  return <ClientScopeCtx.Provider value={value}>{children}</ClientScopeCtx.Provider>;
}

export function useClientScope(): ClientScopeValue {
  return useContext(ClientScopeCtx);
}

export const CLIENT_SCOPE_STORAGE_KEY = STORAGE_KEY;
export const CLIENT_SCOPE_ALL = ALL_SENTINEL;
